import type { Dispatch, SetStateAction } from 'react'
import type { TranscriptItem } from 'types'
import { CONTACT_CONFIG } from '../config/constants.js'
import { formatBookingLinkResponse } from './ensure-booking-link.js'
import { messageHasBookingWidget } from './booking-markdown.js'
import { generateTranscriptId } from './transcript-append.js'

const DURABLE_ATTACHMENT_TYPES = new Set<NonNullable<TranscriptItem['attachment']>['type']>([
  'calendar_widget',
  'discovery_report',
  'research-card',
  'intelligence_briefing',
  'visual_observation',
])

export function createBookingWidgetAttachment(): NonNullable<TranscriptItem['attachment']> {
  return {
    type: 'calendar_widget',
    name: 'Talk to Farzad',
    url: CONTACT_CONFIG.SCHEDULING.BOOKING_URL as string,
  }
}

/** Rows that must survive Live session teardown (booking, report, briefing, visual notes). */
export function hasDurableTranscriptSideEffect(item: TranscriptItem): boolean {
  const attachmentType = item.attachment?.type
  if (attachmentType && DURABLE_ATTACHMENT_TYPES.has(attachmentType)) return true
  if (item.role === 'model' && messageHasBookingWidget(item.text ?? '')) return true
  return false
}

function stampBookingRow(
  prev: TranscriptItem[],
  index: number,
  attachment: NonNullable<TranscriptItem['attachment']>,
): TranscriptItem[] {
  const row = prev[index]
  if (!row) return prev
  const next = [...prev]
  next[index] = {
    ...row,
    text: row.text?.trim() ? row.text : formatBookingLinkResponse(),
    attachment,
    isFinal: true,
    status: 'complete',
    isLiveTranscript: false,
  }
  return next
}

/** New Schedule card. Does not overwrite a finished Live/briefing row. */
export function appendBookingReply(prev: TranscriptItem[]): TranscriptItem[] {
  if (
    prev.some(
      item =>
        item.role === 'model' &&
        (item.attachment?.type === 'calendar_widget' || messageHasBookingWidget(item.text || '')),
    )
  ) {
    return prev
  }
  return [
    ...prev,
    {
      id: generateTranscriptId('assistant'),
      role: 'model',
      text: formatBookingLinkResponse(),
      timestamp: new Date(),
      isFinal: true,
      status: 'complete',
      attachment: createBookingWidgetAttachment(),
    },
  ]
}

export function applyBookingWidgetToTranscript(
  setTranscript: Dispatch<SetStateAction<TranscriptItem[]>>,
  turnId?: string
): void {
  const attachment = createBookingWidgetAttachment()
  setTranscript(prev => {
    if (turnId) {
      const idx = prev.findIndex(
        item => item.role === 'model' && (item.id === turnId || item.turnId === turnId),
      )
      if (idx >= 0) return stampBookingRow(prev, idx, attachment)
    }

    for (let i = prev.length - 1; i >= 0; i -= 1) {
      const row = prev[i]
      if (row?.role !== 'model') continue
      if (row.status === 'streaming' || row.isFinal === false) {
        return stampBookingRow(prev, i, attachment)
      }
    }

    return appendBookingReply(prev)
  })
}
